// Structure Canvas - Visual display of the structure being built
// Shows main chain and branches with SNFG shapes

import { Icon } from 'semantic-ui-react';
import SNFGsvg from '../../visualizer-tool/glycan-interpreter/snfgSVG';
import BondSelector from './BondSelector';
import '../../css/advanced-search.css';

export default function PatternCanvas({ pattern, onRemoveSugar, onClearPattern, onUpdateBond }) {
  if (!pattern || !pattern.mainChain || pattern.mainChain.length === 0) {
    return (
      <div className="pattern-canvas empty">
        <div className="empty-pattern-message">
          <Icon name="mouse pointer" size="big" style={{ color: '#9e9e9e' }} />
          <h3>Your Structure Preview</h3>
          <p>Click sugars from the palette below to build your structure</p>
          <div className="pattern-examples">
            <strong>Examples:</strong>
            <ul>
              <li>Click: Glc → Man → Gal</li>
              <li>Click: Glc → * (wildcard) → Man</li>
              <li>Type: "Glc-Man-Gal" in Text Query tab</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Check if pattern contains unknown sugars
  const hasUnknownSugars = pattern.mainChain.some(sugar => sugar.isUnknown);
  const unknownSugarNames = pattern.mainChain
    .filter(sugar => sugar.isUnknown)
    .map(sugar => sugar.sugar);

  return (
    <div className="pattern-canvas">
      <div className="pattern-header">
        <h3>
          <Icon name="chain" />
          Your Structure
        </h3>
        {pattern.mainChain.length > 0 && (
          <button onClick={onClearPattern} className="clear-pattern-btn">
            <Icon name="trash" />
            Clear All
          </button>
        )}
      </div>

      <div className="pattern-display">
        <div className="main-chain-section">
          <div className="chain-label">Main Chain:</div>
          <div className="chain-container">
            {pattern.mainChain.map((sugar, index) => (
              <div key={sugar.id} className="sugar-in-chain">
                <div className="sugar-item">
                  {sugar.isWildcard ? (
                    <div className="wildcard-symbol">
                      <Icon name="asterisk" size="big" />
                    </div>
                  ) : (
                    <div className="sugar-shape" style={{ position: 'relative' }}>
                      <SNFGsvg snfg={sugar.sugar} />
                      {sugar.isUnknown && (
                        <Icon
                          name="warning sign"
                          color="orange"
                          size="small"
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            backgroundColor: 'white',
                            borderRadius: '50%'
                          }}
                          title={`Unknown sugar: ${sugar.sugar}`}
                        />
                      )}
                    </div>
                  )}
                  <div className="sugar-name" style={{ color: sugar.isUnknown ? '#ff6b6b' : 'inherit' }}>
                    {sugar.sugar}
                  </div>
                  <button
                    className="remove-sugar-btn"
                    onClick={() => onRemoveSugar(sugar.id)}
                    title="Remove sugar"
                  >
                    <Icon name="close" size="small" />
                  </button>
                </div>
                {index < pattern.mainChain.length - 1 && (
                  <div className="sugar-connector">
                    {onUpdateBond ? (
                      <BondSelector
                        sugarIndex={index}
                        bond={pattern.bonds && pattern.bonds[index]}
                        onBondChange={onUpdateBond}
                        fromSugar={sugar}
                        toSugar={pattern.mainChain[index + 1]}
                      />
                    ) : (
                      <Icon name="minus" />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {pattern.branches && pattern.branches.length > 0 && (
          <div className="branches-section">
            <div className="chain-label">Branches:</div>
            {pattern.branches.map((branch, branchIndex) => (
              <div key={branch.id} className="branch-display">
                <div className="branch-info">
                  <Icon name="fork" />
                  <span className="branch-label">Branch {branchIndex + 1}</span>
                  <span className="branch-attachment">
                    → {pattern.mainChain[branch.attachToPosition]?.sugar}
                    {branch.attachmentBond && (
                      <span className="attachment-bond-badge">
                        {' '}via {branch.attachmentBond.anomeric || ''}
                        {branch.attachmentBond.from || 1}→{branch.attachmentBond.to}
                      </span>
                    )}
                  </span>
                </div>
                <div className="branch-chain">
                  {branch.sugars.map((sugar, sugarIndex) => (
                    <div key={sugar.id} className="sugar-in-chain">
                      <div className="sugar-item small">
                        <div className="sugar-shape small">
                          <SNFGsvg snfg={sugar.sugar} />
                        </div>
                        <div className="sugar-name small">{sugar.sugar}</div>
                      </div>
                      {sugarIndex < branch.sugars.length - 1 && (
                        <div className="sugar-connector small">
                          {branch.bonds && branch.bonds[sugarIndex] ? (
                            <span className="bond-label-small">
                              {branch.bonds[sugarIndex].anomeric || ''}
                              {branch.bonds[sugarIndex].from || 1}→{branch.bonds[sugarIndex].to}
                            </span>
                          ) : (
                            <Icon name="minus" />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pattern-stats">
        <Icon name="info circle" />
        <span>
          {pattern.mainChain.length} sugar{pattern.mainChain.length !== 1 ? 's' : ''} in main chain
          {pattern.branches && pattern.branches.length > 0 && (
            <>, {pattern.branches.length} branch{pattern.branches.length !== 1 ? 'es' : ''}</>
          )}
        </span>
      </div>

      {hasUnknownSugars && (
        <div style={{
          marginTop: '10px',
          padding: '10px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Icon name="warning circle" color="orange" />
          <div style={{ flex: 1 }}>
            <strong>Unknown sugar{unknownSugarNames.length > 1 ? 's' : ''}:</strong> {unknownSugarNames.join(', ')}
            <div style={{ fontSize: '0.9em', marginTop: '4px', color: '#856404' }}>
              These sugars are not in the database library. The search may still work if the backend can match them, but results might be limited.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
